import { IMConstants } from '../../../common/utils/IMConstants';
import { IntegrityMonitorPlatform } from '../../../common/utils/types';

export type CustomRuleValidator<T> = (input: T) => { valid: boolean; error: string };
export type AsyncCustomRuleValidator<T> = (input: T) => Promise<{ valid: boolean; error: string }>;

const valid = { valid: true, error: '' };
const invalid = (error: string) => ({ valid: false, error });

export interface ValidationErrors {
  errorPathTooLong: string;
  errorPathRestricted: string;
  errorTooManyWildcards: string;

  errorInvalidUnixPath: string;
  errorInvalidWindowsFilePath: string;
  errorInvalidWindowsDirPath: string;

  errorRegistryKeyTooLong: string;
  errorRegistryKeyRestricted: string;
  errorInvalidRegistryKey: string;

  errorRegistryValueTooLong: string;
  errorRegistryValueRestricted: string;
  errorInvalidRegistryValue: string;
}

export function getFileRuleInvalidExtensions(): string[] {
  return ['.aodl', '.pf', '.log', 'log.txt', '.tmp', '.evtx'];
}

export class CustomRuleValidators {
  static pathRestrictors: any = ['/', '/*', '**/'];
  static registryKeyRestrictors: any = [];

  static registryKeyRegex =
    /^(HKEY_CLASSES_ROOT|HKCR|HKEY_CURRENT_USER|HKCU|HKEY_LOCAL_MACHINE|HKLM|HKEY_USERS|HKU|HKEY_CURRENT_CONFIG|HKCC)\\([a-zA-Z\d\s_@\-^!#.:$%&+={}\[\]\\*])+$/;
  static windowsDriveRegex = /^[a-zA-Z]:\\+$/;
  static windowsFileRegex = /^[a-zA-Z]:\\(?:[^\\\/:?"<>|\r\n]+\\)*[^\\\/:?"<>|\r\n]*$/;
  static windowsDirRegex = /^[a-zA-Z]:\\(?:[^\\\/:?"<>|\r\n]+\\)*[^\\\/:?"<>|\r\n.]*$/;

  static pathValidator(
    getOs: () => IntegrityMonitorPlatform,
    directory: boolean,
    shouldValidateWildcards: boolean,
    translations: ValidationErrors
  ): CustomRuleValidator<string> {
    return (input: string) => {
      if (input.length > IMConstants.INTEGRITY_MONITOR_RULE_FILE_MAX_LENGTH_KEY) {
        return invalid(translations.errorPathTooLong);
      }

      // check restrictors list
      if (this.pathRestrictors.find((x: string) => x === input) || this.windowsDriveRegex.test(input)) {
        return invalid(translations.errorPathRestricted);
      }

      const os: IntegrityMonitorPlatform = getOs();
      // validate unix path
      if ((os === IntegrityMonitorPlatform.LINUX || os === IntegrityMonitorPlatform.OSX) && !input.startsWith('/')) {
        return invalid(translations.errorInvalidUnixPath);
      }

      // validate windows path
      if (os === IntegrityMonitorPlatform.WINDOWS) {
        if (!directory && !this.windowsFileRegex.test(input)) return invalid(translations.errorInvalidWindowsFilePath);
        if (directory && !this.windowsDirRegex.test(input)) return invalid(translations.errorInvalidWindowsDirPath);
      }

      // validate number of wildcards
      if (shouldValidateWildcards && input.split('*').length > 1) {
        return invalid(translations.errorTooManyWildcards);
      }

      return valid;
    };
  }

  static registryKeyValidator(translations: ValidationErrors): CustomRuleValidator<string> {
    return (input: string) => {
      if (input.length > IMConstants.INTEGRITY_MONITOR_RULE_REGISTRYKEY_MAX_LENGTH_KEY) {
        return invalid(translations.errorRegistryKeyTooLong);
      }

      if (this.registryKeyRestrictors.find((x: string) => x === input)) {
        return invalid(translations.errorRegistryKeyRestricted);
      }

      if (!this.registryKeyRegex.test(input)) {
        return invalid(translations.errorInvalidRegistryKey);
      }

      return valid;
    };
  }

  static registryValueValidator(translations: ValidationErrors): CustomRuleValidator<string> {
    return (input: string) => {
      if (input.length > IMConstants.INTEGRITY_MONITOR_RULE_REGISTRYVALUE_MAX_LENGTH_VALUE) {
        return invalid(translations.errorRegistryValueTooLong);
      }

      if (this.registryKeyRestrictors.find((x: string) => x === input)) {
        return invalid(translations.errorRegistryValueRestricted);
      }

      if (!this.registryKeyRegex.test(input)) {
        return invalid(translations.errorInvalidRegistryValue);
      }

      return valid;
    };
  }

  static atLeastOneValidator(error: string): AsyncCustomRuleValidator<any[]> {
    return async (xs: any[]) => (xs.length > 0 ? { valid: true, error: '' } : { valid: false, error });
  }

  static atMostValidator(n: number, error: string): AsyncCustomRuleValidator<any[]> {
    return async (xs: any[]) => (xs.length > n ? { valid: false, error } : { valid: true, error: '' });
  }
}
